import { useCallback, useEffect, useState } from 'react';
import { usePageLoading } from './motion/PetalLoader';
import { useNavigate, useParams } from 'react-router-dom';
import { CalendarX, ChevronRight } from 'lucide-react';
import PageBackHeader from './ui/PageBackHeader';
import Button from './ui/Button';
import FestivalMatchSetupModal from './lineup/FestivalMatchSetupModal';
import { useCurrentUserProfile } from '../contexts/CurrentUserProfileContext';
import { useEventDetail } from '../hooks/queries/useEventDetail';
import { useMyArtistVotesByDay } from '../hooks/queries/useEventLineup';
import { resolveEventDisplayName } from '../utils/eventDisplayName';
import { formatDayLabelLong, formatDayToggleParts } from '../utils/lineupFormat';
import { goBackTo } from '../lib/navHistory';

import { usePromptAuth } from './auth/AuthPrompt';
const PREVIEW_PICKS = 3;

export default function PicksSummaryPage() {
  const { eventId, dayIndex } = useParams();
  const navigate = useNavigate();
  const promptAuth = usePromptAuth();
  const { session } = useCurrentUserProfile();
  const userId = session?.user?.id;

  const { data: event } = useEventDetail(eventId);
  const { byDay, totalPicks, isLoading } = useMyArtistVotesByDay(eventId, userId);
  const pageLoad = usePageLoading(Boolean(session) && isLoading, { latch: Boolean(session) });

  const [editor, setEditor] = useState(null);
  const [openedFromRoute, setOpenedFromRoute] = useState(false);

  useEffect(() => {
    if (openedFromRoute || isLoading || dayIndex == null || byDay.length === 0) return;
    const idx = Number.parseInt(dayIndex, 10);
    if (Number.isNaN(idx) || idx < 0 || idx >= byDay.length) return;
    const bucket = byDay[idx];
    if (bucket.isAttending) {
      setEditor({ focusDay: bucket.day, dayIndex: idx });
    } else {
      setEditor({ step: 'days' });
    }
    setOpenedFromRoute(true);
  }, [dayIndex, byDay, isLoading, openedFromRoute]);

  const handleBack = useCallback(() => {
    goBackTo(navigate, `/events/${eventId}`);
  }, [navigate, eventId]);

  const openDayEditor = (bucket, index) => {
    navigate(`/events/${eventId}/picks/${index}`, { replace: true });
    setEditor({ focusDay: bucket.day, dayIndex: index });
  };

  const eventName = event ? resolveEventDisplayName(event) : null;

  if (!session) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 text-center">
        <PageBackHeader title="My picks" onBack={handleBack} backLabel="Back to event" />
        <p className="mb-6 text-sm text-ink/60">Log in to see your picks.</p>
        <Button size="lg" onClick={() => promptAuth()}>
          Log in
        </Button>
      </div>
    );
  }

  if (pageLoad.show) return pageLoad.node; // only the loader until the page can show whole (Sam, 2026-10-20)

  if (byDay.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-6">
        <PageBackHeader title="My picks" onBack={handleBack} backLabel="Back to event" />
        <p className="py-12 text-center text-sm text-ink/60">No lineup announced yet.</p>
      </div>
    );
  }

  const attendingCount = byDay.filter((bucket) => bucket.isAttending).length;

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      <PageBackHeader title="My picks" onBack={handleBack} backLabel="Back to event" />

      <p className="mb-4 lineup-desc">
        {totalPicks === 0
          ? 'Pick the sets you care about — we use them to match your group.'
          : `${totalPicks} pick${totalPicks !== 1 ? 's' : ''} across ${attendingCount} day${
              attendingCount !== 1 ? 's' : ''
            }.`}
      </p>

      <div>
        {byDay.map((bucket, index) => {
          const preview = bucket.picks.slice(0, PREVIEW_PICKS);
          const { name } = formatDayToggleParts(bucket.day);

          if (!bucket.isAttending) {
            return (
              <div
                key={bucket.day ?? 'no-day'}
                className="pick-day-card flex items-center gap-3 opacity-60"
              >
                <CalendarX className="h-5 w-5 shrink-0 text-ink/40" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="pick-day-card__name truncate">{formatDayLabelLong(bucket.day)}</p>
                  <p className="text-micro text-ink/50">Not going this day</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setEditor({ step: 'days' })}>
                  Change
                </Button>
              </div>
            );
          }

          return (
            <button
              key={bucket.day ?? 'no-day'}
              type="button"
              onClick={() => openDayEditor(bucket, index)}
              className="pick-day-card w-full text-left transition-opacity hover:opacity-90"
            >
              <div className="flex items-center gap-2">
                <span className="pick-day-card__name min-w-0 flex-1 truncate">{name}</span>
                <span className="picked-pill shrink-0">{bucket.picks.length}</span>
                <ChevronRight className="pick-day-card__chevron h-5 w-5 shrink-0" aria-hidden />
              </div>

              {bucket.picks.length > 0 && (
                <div className="mt-2.5 flex flex-wrap gap-[7px]">
                  {preview.map((pick) => (
                    <span key={pick.artist_id} className="pick-day-card__chip">
                      {pick.artist_name}
                    </span>
                  ))}
                  {bucket.picks.length > preview.length && (
                    <span className="pick-day-card__chip text-ink/60">
                      +{bucket.picks.length - preview.length}
                    </span>
                  )}
                </div>
              )}

              {bucket.picks.length === 0 && (
                <p className="mt-2 text-micro italic text-[#5a5170]">
                  No picks yet — tap to choose.
                </p>
              )}
            </button>
          );
        })}
      </div>

      <FestivalMatchSetupModal
        isOpen={editor !== null}
        onClose={() => {
          setEditor(null);
          if (dayIndex != null) navigate(`/events/${eventId}/picks`, { replace: true });
        }}
        eventId={eventId}
        eventName={eventName}
        userId={userId}
        mode="edit"
        startStep={editor?.step ?? 'votes'}
        focusDay={editor?.focusDay ?? null}
        onComplete={() => {
          setEditor(null);
          if (dayIndex != null) navigate(`/events/${eventId}/picks`, { replace: true });
        }}
      />
    </div>
  );
}
